import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { LetModule } from '@ngrx/component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';
import { BusinessPlanService } from '@app/modules/account-settings/pages/business-profile/services/business-plan.service';
import { Promotion } from '@modules/promotion/models/promotion.model';
import { PromotionApplicationCheckResultType, PromotionType } from '@generated/graphql';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { CheckoutFlowFacadeService } from '@shared/component/payments/services/checkout-flow-facade.service';

@UntilDestroy()
@Component({
  selector: 'app-checkout-promotion-panel',
  standalone: true,
  imports: [
    CommonModule,
    LetModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  templateUrl: './checkout-promotion-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutPromotionPanelComponent implements OnDestroy {
  @Input() visible = false;
  @Input() set orderId(value: string | null) {
    if (this._orderId === value) {
      return;
    }

    this._orderId = value;
    this.resetPromotionPanelState();
  }

  @ViewChild('promoCodeInput') promoCodeInput: ElementRef<HTMLInputElement>;

  promoCodeVisible = false;
  promoCodeForm = this.fb.group({
    promoCode: [''],
  });
  isCheckingPromo = false;
  activePromotion: Promotion | null = null;
  promotionApplicationCheckResult: PromotionApplicationCheckResultType | null = null;

  private _orderId: string | null = null;

  constructor(
    private readonly businessPlanService: BusinessPlanService,
    private readonly fb: FormBuilder,
    private readonly checkoutFlowFacade: CheckoutFlowFacadeService,
    private readonly snackBarService: SnackBarService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.checkoutFlowFacade.activePromotion$.pipe(
      tap((promotion) => {
        this.activePromotion = promotion;
        this.cdr.markForCheck();
      }),
      untilDestroyed(this),
    ).subscribe();

    this.checkoutFlowFacade.isCheckingPromotion$.pipe(
      tap((isCheckingPromotion) => {
        this.isCheckingPromo = isCheckingPromotion;
        this.cdr.markForCheck();
      }),
      untilDestroyed(this),
    ).subscribe();

    this.checkoutFlowFacade.promotionApplicationCheckResult$.pipe(
      tap((promotionApplicationCheckResult) => {
        this.promotionApplicationCheckResult = promotionApplicationCheckResult;
        this.cdr.markForCheck();
      }),
      untilDestroyed(this),
    ).subscribe();
  }

  get businessPlan$() {
    return this.businessPlanService.businessPlan$;
  }

  get orderId(): string | null {
    return this._orderId;
  }

  ngOnDestroy(): void {
    this.checkoutFlowFacade.resetPromotionState();
  }

  showPromoCode(): void {
    this.promoCodeVisible = true;
    this.cdr.markForCheck();

    setTimeout(() => this.promoCodeInput?.nativeElement?.focus());
  }

  applyPromoCode(): void {
    const promoCodeValue = this.promoCodeForm.get('promoCode')?.value;

    if (!promoCodeValue || !this._orderId) {
      return;
    }

    this.checkoutFlowFacade.applyPromoCode$(this._orderId, promoCodeValue).pipe(
      untilDestroyed(this),
    ).subscribe({
      error: () => {
        this.snackBarService.pushErrorAlert('Failed to apply promo code');
        this.cdr.markForCheck();
      },
    });
  }

  removePromoCode(): void {
    this.checkoutFlowFacade.removePromotion();
    this.promoCodeForm.get('promoCode')?.setValue('');
    this.cdr.markForCheck();
  }

  private resetPromotionPanelState(): void {
    this.promoCodeVisible = false;
    this.promoCodeForm.get('promoCode')?.setValue('');
    this.checkoutFlowFacade.resetPromotionCheckState();
    this.cdr.markForCheck();
  }
}
